import { getSelectedSlas } from '@vtex/delivery-packages/dist/sla'
import { getItemsIndexes } from '@vtex/delivery-packages/dist/items'
import {
  filterLogisticsInfo,
  mergeLogisticsInfos,
} from '@vtex/delivery-packages/dist/shipping'
import {
  selectCheapestSlaForAllItems,
  selectFastestSlaForAllItems,
} from '@vtex/estimate-calculator'
import { isEmpty, isArray, isNumber, isNil, cloneDeep } from 'lodash'

import type { OrderForm } from '../../../core/requests/checkout'
import { DeliveryChannel } from '../constants'

export interface PickupPoints {
  id: string | null | undefined
  slas: SLA[]
}

export const removeEmpty = (arr: LogisticsInfo | null) => {
  if (isNil(arr) || isEmpty(arr) || !isArray(arr)) {
    return []
  }

  return arr.filter((elm) => isNumber(elm) || !isEmpty(elm))
}

export function filterCheckInFromSlas(slas: SLA[], orderForm: OrderForm) {
  return slas.filter((sla) => !isSlaCheckIn(sla, orderForm))
}

export function filterCheckInFromLogisticsInfo(
  slas: LogisticsInfo,
  orderForm: OrderForm
) {
  return slas.filter((sla) => !isSlaCheckIn(sla, orderForm))
}

export function isSlaCheckIn(
  sla: LogisticsInfoItem | SLA,
  orderForm: OrderForm
) {
  const checkedInPickupPointId = orderForm?.checkedInPickupPointId

  return (
    !!orderForm &&
    !!sla &&
    !!orderForm.checkedInPickupPointId &&
    checkedInPickupPointId === sla.pickupPointId
  )
}

export function filterSlasPerCheckIn(
  slas: LogisticsInfo,
  orderForm: OrderForm
) {
  slas = removeEmpty(slas)
  if (slas.length === 0) {
    return []
  }

  return slas.filter((sla) => isSlaCheckIn(sla, orderForm))
}

export function excludeCheckInFromLogisticsInfo(
  logisticsInfo: LogisticsInfo | undefined,
  orderForm: OrderForm
) {
  if (!logisticsInfo) {
    return null
  }

  let slas = getSelectedSlas(logisticsInfo)

  slas = slas.map((sla, index) => sla || { id: null, itemIndex: index })
  const notCheckedInSlas = filterCheckInFromLogisticsInfo(slas, orderForm)

  if (notCheckedInSlas.length === 0) {
    return []
  }

  const notCheckedInSlasMap: Record<number, boolean> = {}

  notCheckedInSlas.forEach((sla) => {
    notCheckedInSlasMap[sla.itemIndex] = true
  })

  return logisticsInfo.filter((li) => !!notCheckedInSlasMap[li.itemIndex])
}

export function getLogisticsInfoFilteredByCheckIn(orderForm: OrderForm) {
  if (
    !orderForm ||
    !orderForm.shippingData ||
    !orderForm.shippingData.logisticsInfo
  ) {
    return []
  }

  const { logisticsInfo } = orderForm.shippingData
  const slas = getSelectedSlas(logisticsInfo)
  const checkedInSlas = filterSlasPerCheckIn(slas, orderForm)

  if (checkedInSlas.length === 0) {
    return []
  }

  const checkedInSlasMap: Record<number, LogisticsInfoItem> = {}

  checkedInSlas.forEach((sla) => {
    checkedInSlasMap[sla.itemIndex] = sla
  })

  return logisticsInfo.filter((li) => !!checkedInSlasMap[li.itemIndex])
}

export function getDeliveryOrPickupAndTakeAwayLogisticsInfo(
  orderForm: OrderForm
) {
  if (
    !orderForm ||
    !orderForm.shippingData ||
    !orderForm.shippingData.logisticsInfo ||
    orderForm.shippingData.logisticsInfo.length === 0
  ) {
    return {
      deliveryOrPickupLogisticsInfo: [],
      takeAwayLogisticsInfo: [],
    }
  }

  const { logisticsInfo } = orderForm.shippingData

  const totalNumberOfItems = logisticsInfo.length
  const takeAwayLogisticsInfo =
    getLogisticsInfoFilteredByCheckIn(orderForm) || []

  let deliveryOrPickupLogisticsInfo = logisticsInfo

  if (!isEmpty(takeAwayLogisticsInfo)) {
    let { otherIndexes: deliveryOrPickupItemsFilters } = getItemsIndexes(
      takeAwayLogisticsInfo,
      totalNumberOfItems
    )

    deliveryOrPickupItemsFilters = deliveryOrPickupItemsFilters.map(
      (itemIndex: number) => ({
        itemIndex,
      })
    )

    deliveryOrPickupLogisticsInfo = filterLogisticsInfo(logisticsInfo, {
      items: deliveryOrPickupItemsFilters,
    })
  }

  return {
    deliveryOrPickupLogisticsInfo,
    takeAwayLogisticsInfo,
  }
}

export function applyRuleForNonTakeAwayItems(
  orderForm: OrderForm | undefined,
  rule: (logisticsInfo: LogisticsInfo) => LogisticsInfo
) {
  if (
    !orderForm ||
    !orderForm.shippingData ||
    !orderForm.shippingData.logisticsInfo ||
    orderForm.shippingData.logisticsInfo.length === 0
  ) {
    return []
  }

  const logisticsInfos = getDeliveryOrPickupAndTakeAwayLogisticsInfo(orderForm)

  let { deliveryOrPickupLogisticsInfo } = logisticsInfos
  const { takeAwayLogisticsInfo } = logisticsInfos

  deliveryOrPickupLogisticsInfo = rule
    ? rule(deliveryOrPickupLogisticsInfo)
    : deliveryOrPickupLogisticsInfo

  const finalLogisticsInfo = mergeLogisticsInfos(
    deliveryOrPickupLogisticsInfo,
    takeAwayLogisticsInfo
  )

  return finalLogisticsInfo
}

export function selectCheapestSlaForNonTakeAwayItems(
  orderForm: OrderForm | undefined
) {
  return applyRuleForNonTakeAwayItems(orderForm, selectCheapestSlaForAllItems)
}

export function selectFastestSlaForNonTakeAwayItems(
  orderForm: OrderForm | undefined
) {
  return applyRuleForNonTakeAwayItems(orderForm, selectFastestSlaForAllItems)
}

function getPickupInPoints(
  deliveryLogisticsInfo: LogisticsInfoItem[],
  orderForm: OrderForm
) {
  const pickupPoints: PickupPoints[] = []

  deliveryLogisticsInfo.forEach((item) => {
    filterCheckInFromSlas(item.slas, orderForm)
      .filter((sla) => sla.deliveryChannel === DeliveryChannel.PickupInPoint)
      .forEach((sla) => {
        const pickupPoint = pickupPoints.find((point) => point.id === sla.id)

        if (!pickupPoint) {
          pickupPoints.push({
            id: sla.id,
            slas: [sla],
          })
        } else {
          pickupPoint.slas.push(sla)
        }
      })
  })

  pickupPoints.sort((a, b) => b.slas.length - a.slas.length)

  return pickupPoints
}

export function selectPickup(
  logisticsInfo: LogisticsInfo | undefined,
  orderForm: OrderForm | undefined
) {
  if (!orderForm || !logisticsInfo) {
    return
  }

  const newLogisticsInfo = cloneDeep(logisticsInfo)

  const deliveryLogisticsInfo =
    excludeCheckInFromLogisticsInfo(newLogisticsInfo, orderForm) ?? []

  const pickupPoints = getPickupInPoints(deliveryLogisticsInfo, orderForm)

  if (newLogisticsInfo) {
    newLogisticsInfo.forEach((item) => {
      const isTakeAway = !deliveryLogisticsInfo.find(
        (current) => current.itemIndex === item.itemIndex
      )

      if (isTakeAway) {
        return
      }

      const availablePickupPoint = pickupPoints.find(
        (pickupPoint) => !!item.slas.find((sla) => sla.id === pickupPoint.id)
      )

      if (
        availablePickupPoint &&
        item.selectedDeliveryChannel !== DeliveryChannel.PickupInPoint
      ) {
        item.selectedSla = availablePickupPoint.id ?? ''
        item.selectedDeliveryChannel = DeliveryChannel.PickupInPoint
      }
    })
  }

  return newLogisticsInfo
}

export function selectDefaultSla(
  logisticsInfo: LogisticsInfo | undefined,
  orderForm: OrderForm | undefined
) {
  if (!orderForm || !logisticsInfo) {
    return
  }

  const newLogisticsInfo = cloneDeep(logisticsInfo)

  const deliveryLogisticsInfo =
    excludeCheckInFromLogisticsInfo(newLogisticsInfo, orderForm) ?? []

  const pickupPoints = getPickupInPoints(deliveryLogisticsInfo, orderForm)

  if (newLogisticsInfo) {
    newLogisticsInfo.forEach((item) => {
      if (item.selectedSla && item.selectedDeliveryChannel) {
        return
      }

      const isTakeAway = !deliveryLogisticsInfo.find(
        (current) => current.itemIndex === item.itemIndex
      )

      if (isTakeAway) {
        return
      }

      const availablePickupPoint = pickupPoints.find(
        (pickupPoint) => !!item.slas.find((sla) => sla.id === pickupPoint.id)
      )

      if (availablePickupPoint) {
        item.selectedSla = availablePickupPoint.id ?? ''
        item.selectedDeliveryChannel = DeliveryChannel.PickupInPoint
      }
    })
  }

  return newLogisticsInfo
}

export function excludeCheckInFromSlas(slas: SLA[], orderForm: OrderForm) {
  return slas.filter((sla) => !isSlaCheckIn(sla, orderForm))
}
